import { breakpointSizes, getBreakpointDown } from '@paywithextend/components'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { matchPath } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectPatchingOrganizationInviteFromState,
  selectPatchingOrganizationInviteTopLevelErrorFromState,
} from 'src/app/OrganizationInvites/redux/organizationInvites.selectors'
import { DeclineOrganizationInviteMessage } from 'src/app/Toast/Toast'
import AcceptOrgInvitationConfirmation from 'src/components/ManageOrgInvite/AcceptOrgInvitationConfirmation'
import OrgInvitationForm from 'src/components/ManageOrgInvite/OrgInvitationForm'
import NotFoundPopupContent from 'src/components/NotFoundPopupContent/NotFoundPopupContent'
import Popup from 'src/components/Popup/Popup'
import { CreditCardType } from 'src/models/creditCard.models'
import { OrganizationsListResponse } from 'src/models/organizations.models'
import { OrgInviteStatus } from 'src/models/people.models'
import { closeModal, connectedPush } from 'src/navigation/Lnk'
import { virtualCardDetailRouteProps } from 'src/navigation/routeProps'
import { manageOrgInviteUrl, yourCardsUrl } from 'src/navigation/urls'
import { createSetPerSessionValues } from 'src/redux/actions/auth.actions'
import { getCreditCards } from 'src/redux/actions/data/creditCards.actions'
import {
  createAbandonOrganizationInviteAction,
  getUserOrganizationInvites,
  putOrganizationInvite,
} from 'src/redux/actions/data/organizations.actions'
import { selectNonReadOnlyCreditCardListModelsFromState } from 'src/redux/selectors/data/creditCards.selectors'
import { selectOrganizationInviteFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { css } from 'styled-components'

const ManageOrgInvite: React.FC = () => {
  const [hasAcceptedInvite, setHasAcceptedInvite] = useState<boolean>(false)
  const organizationInvite = useSelector(selectOrganizationInviteFromState)
  const [organization, setOrganization] = useState<
    OrganizationsListResponse | undefined
  >(undefined)
  const patchingOrganizationInvite = useSelector(
    selectPatchingOrganizationInviteFromState,
  )
  const patchingOrganizationInviteTopLevelError = useSelector(
    selectPatchingOrganizationInviteTopLevelErrorFromState,
  )
  const creditCards = useSelector(
    selectNonReadOnlyCreditCardListModelsFromState,
  )
  const { analytics } = useAnalyticsContext()
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const isOnVirtualCardDetailPage = !!matchPath(
    calculatedBackgroundLocation.pathname,
    virtualCardDetailRouteProps,
  )

  const dispatch = useDispatch()

  const fetchCards = (page?: number): void => {
    dispatch(
      getCreditCards({
        count: 10,
        page,
        type: CreditCardType.Source,
        sortField: 'activeUpdatedAt',
      }),
    )
  }

  useEffect(() => {
    setOrganization(organizationInvite?.organization)
  }, [])

  const dispatchPutOrganizationInvite = (
    acceptOrganizationInvite: boolean,
  ): void => {
    if (organizationInvite) {
      if (acceptOrganizationInvite) {
        dispatch(
          putOrganizationInvite(
            OrgInviteStatus.Accepted,
            organizationInvite,
            undefined,
            setHasAcceptedInvite,
          ),
        )
        analytics?.track('org invitation - accept invite - button press', {
          screen: manageOrgInviteUrl(organizationInvite?.id),
          interaction: 'button press',
          intent: 'accept invite',
        })
      } else {
        dispatch(
          putOrganizationInvite(
            OrgInviteStatus.Rejected,
            organizationInvite,
            <DeclineOrganizationInviteMessage
              organizationInvite={organizationInvite}
            />,
            setHasAcceptedInvite,
          ),
        )
        analytics?.track('org invitation - decline invite - text link press', {
          screen: manageOrgInviteUrl(organizationInvite?.id),
          interaction: 'text link press',
          intent: 'decline invite',
        })
        dispatch(closeModal())
      }
    }
  }

  const onManageOrgInviteClose = (): void => {
    dispatch(createAbandonOrganizationInviteAction())

    if (hasAcceptedInvite) {
      dispatch(
        getUserOrganizationInvites({
          sortDirection: 'ASC',
          sortField: 'createdAt',
        }),
      )
    }
    // if user is trying to view virtual card, go back if org invite modal is closed
    if (isOnVirtualCardDetailPage) {
      dispatch(connectedPush(yourCardsUrl()))
    }
    if (organizationInvite) {
      analytics?.track('org invitation - dismiss invite - overlay press', {
        screen: manageOrgInviteUrl(organizationInvite.id),
        interaction: 'overlay press',
        intent: 'dismiss invite',
      })
    }
    dispatch(
      createSetPerSessionValues({ isAcceptOrgInvitePopupDismissed: true }),
    )
  }

  useEffect(() => {
    fetchCards()
  }, [])

  return (
    <Popup
      onClose={onManageOrgInviteClose}
      noPadding={!!organizationInvite}
      css={css`
        ${hasAcceptedInvite &&
        css`
          width: 500px;
          ${getBreakpointDown(breakpointSizes.XSM)(css`
            width: 100%;
          `)}
        `}
      `}
      buttonId={'closeOrganizationPopup'}
    >
      {organizationInvite ? (
        <OrgInvitationForm
          organizationInvite={organizationInvite}
          onOrgInvitationFormSubmit={dispatchPutOrganizationInvite}
          patchingOrganizationInvite={patchingOrganizationInvite}
          patchingOrganizationInviteTopLevelError={
            patchingOrganizationInviteTopLevelError
          }
          creditCards={creditCards}
        />
      ) : hasAcceptedInvite && organization ? (
        <AcceptOrgInvitationConfirmation organization={organization} />
      ) : (
        <NotFoundPopupContent />
      )}
    </Popup>
  )
}

export default ManageOrgInvite
